'use client';

// ПОИСК ПО СПРАВКЕ НА ТЕЛЕФОНЕ (ТЗ WEB-PARITY-3 К42, кадр iOS K17-help-sections: кружок-лупа в
// шапке). Статьи приходят с сервера готовой разметкой: тап по лупе раскрывает поле поиска,
// слово отбирает строки по их тексту (`data-q` у <li>). Запросов нет — вся справка уже на странице.
//
// APP-CLAIMS-1020 ред. 7, Р6 (как поиск справки в приложении): пустой отбор — герой «Ничего не найдено» (значок лупы,
// заголовок, подпись — общий KEmpty набора, тот же вид, что пустой список ботов), совпадение в названии и подводке
// строки — жирным (`<b>`, semibold, без фона).
// Ред. 11, Д3 (10.5): список статей не «появляется», а ВЪЕЗЖАЕТ — тап по лупе и каждый отбор по буквам: сдвиг снизу
// на 2 × --row-gap (= 2 × Pad.row телефона) → 0 и прозрачность 0 → 1 длительностью и кривой слоёв кабинета
// (--m-dur / --m-ease, те же, что у стопки — своих чисел нет), без лесенки по строкам; герой «Ничего не найдено» —
// тем же ходом. Прежний кроссфейд 0,2 с переходом вида документа (Р6) снят.
import { useCallback, useEffect, useRef, useState } from 'react';
import { KBareButton, KEmpty, KSearch } from '@scriptera/ui/kit';

/** Лупа — один рисунок на кружок шапки и героя пустого поиска. */
function SearchGlyph({ size = 18, stroke = 2.4 }: { size?: number; stroke?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.8-3.8" />
    </svg>
  );
}

/** Подписи строки, в которых выделяется совпадение. */
const HL_SEL = '.help-item-title, .help-item-desc';

/** Выделить жирным все вхождения `word` (без учёта регистра) в тексте узла; пустое слово — вернуть чистый текст.
 *  Исходный текст хранится в `data-plain` — повторный отбор всегда идёт от него. Узлы строятся DOM-методами (не HTML). */
function markWord(el: HTMLElement, word: string): void {
  // Без выделения узел держит живой текст (React мог его сменить — язык) — он и есть исходный.
  const plain = el.childElementCount === 0 ? (el.textContent ?? '') : (el.dataset.plain ?? el.textContent ?? '');
  el.dataset.plain = plain;
  if (!word) {
    if (el.textContent !== plain || el.childElementCount > 0) el.textContent = plain;
    return;
  }
  const low = plain.toLowerCase();
  const parts: Node[] = [];
  let at = 0;
  for (let i = low.indexOf(word); i >= 0; i = low.indexOf(word, at)) {
    if (i > at) parts.push(document.createTextNode(plain.slice(at, i)));
    const b = document.createElement('b');
    b.textContent = plain.slice(i, i + word.length);
    parts.push(b);
    at = i + word.length;
  }
  if (parts.length === 0) {
    if (el.childElementCount > 0) el.textContent = plain;
    return;
  }
  if (at < plain.length) parts.push(document.createTextNode(plain.slice(at)));
  el.replaceChildren(...parts);
}

/** Класс въезда — на видимых блоках результата (`.help-sec` со словом, карточка разделов без слова, герой «Ничего не
 *  найдено»); вид — help.css `.help-slide-in`. Повторный ход на том же блоке — снятием класса и перечитыванием
 *  раскладки, иначе анимация не перезапускается. Браузер без анимаций (reduce) — класс не ставится. */
const SLIDE_SEL = '.help-sec, .help-secs, .help-search-none';
function slideIn(update: () => void): void {
  update();
  const reduce = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduce) return;
  const blocks = Array.from(document.querySelectorAll<HTMLElement>(SLIDE_SEL)).filter((el) => !el.hidden && el.offsetParent !== null);
  for (const el of blocks) el.classList.remove('help-slide-in');
  if (blocks[0]) void blocks[0].offsetWidth;
  for (const el of blocks) el.classList.add('help-slide-in');
}

export function HelpSearchButton({
  label,
  onClick,
  className = 'k-round',
}: {
  label: string;
  onClick: () => void;
  /** Класс кружка: на странице /help — `k-round`, в шапке листа кабинета — `dr-info`. */
  className?: string;
}) {
  return (
    <KBareButton type="button" className={className} aria-label={label} onClick={onClick}>
      <SearchGlyph />
    </KBareButton>
  );
}

/** Поле поиска над списками + отбор строк. `open` ставит кнопка шапки (HelpSearchButton). */
export function HelpSearch({ placeholder, emptyTitle, emptyHint }: { placeholder: string; emptyTitle: string; emptyHint: string }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [none, setNone] = useState(false);

  // Кнопка шапки живёт в другом поддереве: открытие — событием документа.
  useEffect(() => {
    const on = () => {
      setOpen(true);
      // Тап по лупе — список под полем въезжает (Д3): поле встаёт тем же кадром, список — ходом.
      window.requestAnimationFrame(() => slideIn(() => undefined));
    };
    document.addEventListener('scr-help-search', on);
    return () => {
      document.removeEventListener('scr-help-search', on);
      // Ушли со страницы/закрыли лист справки — признак «ищем» не должен пережить поле.
      document.documentElement.removeAttribute('data-help-q');
    };
  }, []);

  // Отбор строк по слову: прячет лишние, выделяет совпадение, помечает строку словом (`data-hlw`) — по метке сторож
  // ниже видит строки, пришедшие ПОСЛЕ ввода (лист кабинета дорисовывает статьи, когда приходит /api/help).
  const wordRef = useRef('');
  const filter = useCallback((word: string): void => {
    const rows = document.querySelectorAll<HTMLElement>('[data-q]');
    let shown = 0;
    rows.forEach((li) => {
      const hit = !word || (li.dataset.q ?? '').includes(word);
      li.hidden = !hit;
      if (hit) shown += 1;
      li.querySelectorAll<HTMLElement>(HL_SEL).forEach((el) => markWord(el, hit ? word : ''));
      li.dataset.hlw = word;
    });
    // Пока слова нет — список разделов как был; со словом — ищем по статьям всех разделов.
    document.documentElement.toggleAttribute('data-help-q', word !== '');
    setNone(word !== '' && shown === 0);
  }, []);

  // Строки дорисовались после ввода (данные пришли позже) — тот же отбор для них, без въезда. Свои правки отбора
  // сторож не перезапускают: у обработанной строки метка `data-hlw` уже равна слову.
  useEffect(() => {
    if (!open) return;
    const mo = new MutationObserver(() => {
      const word = wordRef.current;
      const stale = Array.from(document.querySelectorAll<HTMLElement>('[data-q]')).some((li) => (li.dataset.hlw ?? '') !== word);
      if (stale) filter(word);
    });
    mo.observe(document.body, { childList: true, subtree: true });
    return () => mo.disconnect();
  }, [open, filter]);

  // Отбор — рукой ввода: строки живут в DOM сервера, состояние только у поля.
  const apply = (next: string): void => {
    setQ(next);
    const word = next.trim().toLowerCase();
    wordRef.current = word;
    slideIn(() => filter(word));
  };

  if (!open) return null;
  return (
    <div className="help-search">
      <KSearch className="help-search-f" value={q} onChange={apply} placeholder={placeholder} autoFocus />
      {none ? <KEmpty className="help-search-none" icon={<SearchGlyph size={56} stroke={1.8} />} title={emptyTitle} note={emptyHint} /> : null}
    </div>
  );
}

/** Рука кнопки шапки: просит поле раскрыться. */
export function askHelpSearch(): void {
  document.dispatchEvent(new CustomEvent('scr-help-search'));
}
